import React, { useState } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, Loader2, ArrowLeft, CheckCircle2, Mail, Lock } from 'lucide-react';
import { AnmeldeRahmen } from '../components/anmeldung/AnmeldeRahmen';
import { Titel, Unterzeile, Beschriftung, Feld, Auge, Hauptknopf, Fehlerkasten, TextLink } from '../components/anmeldung/Bausteine';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const checkStrength = (pw) => {
  let s = 0;
  if (pw.length >= 8) s += 25;
  if (/[a-z]/.test(pw)) s += 25;
  if (/[A-Z]/.test(pw)) s += 25;
  if (/[0-9]/.test(pw) || /[^a-zA-Z0-9]/.test(pw)) s += 25;
  return s;
};

const extractDetail = (err) => fehlertext(err, 'Etwas ist schiefgelaufen. Bitte erneut versuchen.');

const AccessCode = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw !== pw2) { setError('Die Passwörter stimmen nicht überein'); return; }
    if (checkStrength(pw) < 50) { setError('Bitte ein stärkeres Passwort wählen'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/redeem-access-code`, {
        email: email.trim().toLowerCase(),
        code: code,
        new_password: pw,
      });
      setDone(true);
      toast.success('Passwort gesetzt. Du kannst dich jetzt anmelden.');
    } catch (err) {
      const msg = extractDetail(err);
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <AnmeldeRahmen>
        <div className="space-y-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <Titel>Passwort gesetzt!</Titel>
          <p className="text-[#4b5768]">Du kannst dich jetzt mit deinem neuen Passwort anmelden.</p>
          <Hauptknopf type="button" onClick={() => navigate('/login')} data-testid="access-code-to-login">
            Zur Anmeldung
          </Hauptknopf>
        </div>
      </AnmeldeRahmen>
    );
  }

  return (
    <AnmeldeRahmen>
      <Titel testid="access-code-titel">Zugangscode einlösen</Titel>
      <Unterzeile>
        Du hast von der Verwaltung telefonisch einen Zugangscode erhalten? Hier kannst du damit dein Passwort setzen.
      </Unterzeile>
      <form onSubmit={submit} className="mt-6 space-y-5">
            {error && <Fehlerkasten testid="access-code-error">{error}</Fehlerkasten>}
            <div>
              <Beschriftung htmlFor="ac-email">E-Mail-Adresse</Beschriftung>
              <Feld id="ac-email" type="email" symbol={Mail} value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="access-code-email" />
            </div>
            <div>
              <Beschriftung htmlFor="ac-code">Zugangscode</Beschriftung>
              <Feld id="ac-code" symbol={KeyRound} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required
                     placeholder="XXXX-XXXX" className="font-mono tracking-widest uppercase"
                     data-testid="access-code-input" />
              <p className="mt-1.5 text-xs text-slate-500">
                Der Code enthält keine Null, kein O, keine Eins, kein I und kein L — bei Zweifeln bitte nachfragen. Bindestrich egal.
              </p>
            </div>
            <div>
              <Beschriftung htmlFor="ac-pw">Neues Passwort</Beschriftung>
              <Feld id="ac-pw" type={show ? 'text' : 'password'} symbol={Lock} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="access-code-password"
                rechts={<Auge sichtbar={show} onClick={() => setShow(!show)} testid="access-code-toggle-password" />} />
            </div>
            <div>
              <Beschriftung htmlFor="ac-pw2">Passwort bestätigen</Beschriftung>
              <Feld id="ac-pw2" type={show ? 'text' : 'password'} symbol={Lock} value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="access-code-password-confirm" />
            </div>
            <Hauptknopf type="submit" disabled={loading} data-testid="access-code-submit">
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin" />Wird gesetzt...</>) : 'Passwort setzen'}
            </Hauptknopf>
            <div className="text-center">
              <TextLink to="/login" className="inline-flex items-center" testid="access-code-back-login">
                <ArrowLeft className="h-4 w-4 mr-1" />Zurück zur Anmeldung
              </TextLink>
            </div>
      </form>
    </AnmeldeRahmen>
  );
};

export default AccessCode;
